import {getReviews} from '../api/reviews';
import {getTranslatedReviews} from '../api/translateReviews';
import ReviewsActions from '../actions/reviewsActions';
import ListingActions from '../actions/listingActions';
import EdapActions from '../actions/edapActions';
import {getGeneratedLocaleFormatted, getLanguage} from '../utils/localeUtils';
import get from 'lodash.get';

function getReviewsRelatedDataFromStore(store) {
    const state = store.getState();
    const {reviewsReducer, systemReducer} = state;
    const reviewsData = reviewsReducer.toJS();
    const {pageIndex, reviewCount} = reviewsData;
    const {reviewPageSize, locale, isMobile} = systemReducer;
    const pageStartIndex = (pageIndex - 1) * reviewPageSize;
    const pageEndIndex =
        pageIndex * reviewPageSize <= reviewCount ? pageIndex * reviewPageSize : reviewCount;
    const siteLanguage = getLanguage(locale);
    const formattedLocale = getGeneratedLocaleFormatted(locale);

    return {
        isMobile,
        locale,
        siteLanguage,
        formattedLocale,
        pageIndex,
        pageStartIndex,
        pageEndIndex,
        reviewPageSize,
        totalReviewsCount: reviewCount,
        reviewsData
    };
}

function isNonSiteLangReviewPresent(reviews) {
    const isAnyReviewInNonSiteLang = reviews.some((review) => {
        return review.isNonSiteLanguageReview;
    });
    return isAnyReviewInNonSiteLang;
}

function dispatchFetchReviewsSuccessEvents(response, store) {
    const state = store.getState();
    store.dispatch(ReviewsActions.scrollTop(false));
    store.dispatch(ReviewsActions.fetchReviewsSuccess(response.body, state.filter));
}

function dispatchFetchReviewErrorEvents(store, error) {
    store.dispatch(ReviewsActions.scrollTop(false));
    store.dispatch(ReviewsActions.fetchReviewsFailure(error));
}

function dispatchAutoTranslatedEvents(store) {
    store.dispatch(ReviewsActions.scrollTop(false));
    store.dispatch(ReviewsActions.updateFetchAutoTranslatedReviews(true));
}

async function fetchReviews(
    listingId,
    filter,
    site,
    siteLanguage,
    formattedLocale,
    page,
    reviewPageSize,
    store
) {
    try {
        const fetchReviewsResponse = await getReviews({
            listingId,
            filter,
            site,
            siteLanguage,
            formattedLocale,
            page,
            reviewPageSize
        });
        return fetchReviewsResponse;
    } catch (error) {
        dispatchFetchReviewErrorEvents(store, error);
        return null;
    }
}

function preTranslatedReviewsAvailable(reviewsData, page) {
    return (
        reviewsData.translatedReviewsMap[page] &&
        reviewsData.translatedReviewsMap[page].length !== 0
    );
}

async function fetchTranslatedReviews(store, page, reviewList, siteLanguage) {
    try {
        const translationResponse = await getTranslatedReviews({
            reviews: reviewList,
            language: siteLanguage
        });
        store.dispatch(ReviewsActions.translateReviewsSuccess(translationResponse.body, page));
        return true;
    } catch (error) {
        dispatchFetchReviewErrorEvents(store, error);
        return false;
    }
}

async function nextPage(store) {
    const state = store.getState();
    const {listingReducer, systemReducer} = state;
    const {
        siteLanguage,
        formattedLocale,
        reviewsData,
        pageIndex,
        reviewPageSize,
        totalReviewsCount
    } = getReviewsRelatedDataFromStore(store);

    const {reviews, filter} = reviewsData;
    const {siteName} = systemReducer;
    const nextPageNumber = pageIndex + 1;
    const nextPageReviewsCount = nextPageNumber * reviewPageSize;
    const reviewsLength = reviews.length;

    let reviewListForNextPage = [];
    let fetchReviewsResponse = null;

    if (nextPageReviewsCount <= reviewsLength || reviewsLength === totalReviewsCount) {
        const pageStartIndex = pageIndex * reviewPageSize;
        const pageEndIndex =
            nextPageReviewsCount <= totalReviewsCount ? nextPageReviewsCount : totalReviewsCount;
        reviewListForNextPage = reviews.slice(pageStartIndex, pageEndIndex);
    } else {
        store.dispatch(ReviewsActions.fetchReviewsStarted());
        fetchReviewsResponse = await fetchReviews(
            listingReducer.listingId,
            filter,
            siteName,
            siteLanguage,
            formattedLocale,
            nextPageNumber,
            reviewPageSize,
            store
        );
        reviewListForNextPage = get(fetchReviewsResponse, 'body.reviews', null);
        if (reviewListForNextPage === null) {
            return;
        }
    }

    if (reviewsData.fetchAutoTranslatedReviews) {
        const isTranslationRequired = isNonSiteLangReviewPresent(reviewListForNextPage);
        if (isTranslationRequired && !preTranslatedReviewsAvailable(reviewsData, nextPageNumber)) {
            store.dispatch(ReviewsActions.fetchReviewsStarted());
            await fetchTranslatedReviews(
                store,
                nextPageNumber,
                reviewListForNextPage,
                siteLanguage
            );
        }
    }
    if (fetchReviewsResponse) {
        dispatchFetchReviewsSuccessEvents(fetchReviewsResponse, store);
    }
    store.dispatch(ReviewsActions.setPageIndex(nextPageNumber));
}

async function previousPage(store) {
    const {reviewsData, pageIndex, reviewPageSize, siteLanguage} = getReviewsRelatedDataFromStore(
        store
    );
    if (pageIndex > 1) {
        const pageStartIndex = (pageIndex - 2) * reviewPageSize;
        const pageEndIndex = (pageIndex - 1) * reviewPageSize;
        const reviewListForPreviousPage = reviewsData.reviews.slice(pageStartIndex, pageEndIndex);
        const isTranslationRequired = isNonSiteLangReviewPresent(reviewListForPreviousPage);
        if (
            reviewsData.fetchAutoTranslatedReviews &&
            isTranslationRequired &&
            !preTranslatedReviewsAvailable(reviewsData, pageIndex - 1)
        ) {
            store.dispatch(ReviewsActions.fetchReviewsStarted());
            await fetchTranslatedReviews(
                store,
                pageIndex - 1,
                reviewListForPreviousPage,
                siteLanguage
            );
        }
        store.dispatch(ReviewsActions.setPageIndex(pageIndex - 1));
    }
}

async function fetchTranslatedReviewsForPage(
    store,
    reviewsData,
    pageNum,
    reviewPageSize,
    siteLanguage
) {
    const {reviews, reviewCount} = reviewsData;
    store.dispatch(ReviewsActions.fetchReviewsStarted());
    const pageStart = (pageNum - 1) * reviewPageSize;
    const pageEnd =
        pageNum * reviewPageSize <= reviewCount ? pageNum * reviewPageSize : reviewCount;
    const reviewListForCurrentPage = reviews.slice(pageStart, pageEnd);

    const translatedReviewsSuccess = await fetchTranslatedReviews(
        store,
        pageNum,
        reviewListForCurrentPage,
        siteLanguage
    );

    return translatedReviewsSuccess;
}

async function translateAlreadyLoadedReviews(store, reviewsData, reviewPageSize, siteLanguage) {
    const {pageIndex} = reviewsData;
    let pageNum;
    let successFlag = true;
    for (pageNum = 1; pageNum < pageIndex; pageNum++) {
        if (!preTranslatedReviewsAvailable(reviewsData, pageNum)) {
            const translatedReviewsSuccess = await fetchTranslatedReviewsForPage(
                store,
                reviewsData,
                pageNum,
                reviewPageSize,
                siteLanguage
            );
            successFlag = successFlag && translatedReviewsSuccess;
        }
    }
    return successFlag;
}

async function translateReviews(store) {
    const {
        isMobile,
        siteLanguage,
        reviewsData,
        pageIndex,
        reviewPageSize
    } = getReviewsRelatedDataFromStore(store);

    let successFlag = true;
    if (isMobile) {
        successFlag = await translateAlreadyLoadedReviews(
            store,
            reviewsData,
            reviewPageSize,
            siteLanguage
        );
    }
    if (preTranslatedReviewsAvailable(reviewsData, pageIndex)) {
        dispatchAutoTranslatedEvents(store);
        return;
    }
    const translatedReviewsSuccess = await fetchTranslatedReviewsForPage(
        store,
        reviewsData,
        pageIndex,
        reviewPageSize,
        siteLanguage
    );

    if (successFlag && translatedReviewsSuccess) {
        store.dispatch(ReviewsActions.updateFetchAutoTranslatedReviews(true));
    }
}

function showOriginalReviews(store) {
    store.dispatch(ReviewsActions.updateFetchAutoTranslatedReviews(false));
}

function viewAllReviews(store, action = {payload: {}}) {
    const {uuid} = action.payload;
    const reviewId = typeof uuid === 'string' && uuid.length > 0 ? uuid : '';

    store.dispatch(ReviewsActions.expandReviewClicked(reviewId));
    store.dispatch(ListingActions.setReviewsModalOpen(true));
    store.dispatch(
        EdapActions.triggerEdapEvent('generic.modal', {
            modalname: 'reviews',
            modalaction: 'open'
        })
    );
}

function generateFilter(ratingArray, filter) {
    // Some user events do not pass ratingArray as they are not interacting
    // with the the star rating filters, thus use the state filter passed in instead
    if (!ratingArray) {
        return filter;
    }
    return ratingArray
        .reduce((parameters, rating) => {
            if (rating > -1) {
                parameters.push(`filterBy=Rating.${rating}`);
            }

            return parameters;
        }, [])
        .join('&');
}

export default {
    generateFilter,
    viewAllReviews,
    nextPage,
    previousPage,
    translateReviews,
    showOriginalReviews
};
export {
    generateFilter,
    viewAllReviews,
    nextPage,
    previousPage,
    translateReviews,
    showOriginalReviews
};
